import { Component, ElementRef, OnDestroy, afterNextRender, inject } from '@angular/core';
import { gsap } from '../../../../../motion/gsap';
import { QUERIES } from '../../../../../motion/motion-env';
import { MotionService } from '../../../../../motion/motion.service';

@Component({
  selector: 'app-home-band-road',
  templateUrl: './home-band-road.component.html',
  styleUrl: './home-band-road.component.css',
})
export class HomeBandRoadComponent implements OnDestroy {
  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private readonly motion = inject(MotionService);
  private mm?: ReturnType<typeof gsap.matchMedia>;
  private destroyed = false;

  constructor() {
    afterNextRender(() => this.motion.whenNeeded(this.host, () => {
      if (!this.destroyed) this.setupScene();
    }));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.mm?.revert();
  }

  private setupScene(): void {
    const section = this.host.querySelector<HTMLElement>('.band--road');
    const media = this.host.querySelector<HTMLElement>('.band-media');
    const content = this.host.querySelector<HTMLElement>('.band-content');
    const photo = this.host.querySelector<SVGSVGElement>('.road-photo');
    const dim = this.host.querySelector<HTMLElement>('.road-dim');
    const forwardCar = this.host.querySelector<SVGGElement>('.road-car--forward');
    const reverseCar = this.host.querySelector<SVGGElement>('.road-car--reverse');
    if (!section || !media || !content || !photo || !dim || !forwardCar || !reverseCar) return;

    this.mm = gsap.matchMedia(this.host);
    this.mm.add({ motion: QUERIES.motion, mobile: QUERIES.mobile }, (ctx) => {
      if (!ctx.conditions!['motion']) return;
      const mobile = !!ctx.conditions!['mobile'];

      // The image overscans by 20% above and below. Travel stays inside that margin.
      // Photo and text have separate depth. The reveal completes just before the section is centred.
      gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: section,
          start: 'top bottom',
          end: 'bottom top',
          scrub: mobile ? 0.4 : 0.75,
          invalidateOnRefresh: true,
        },
      })
        .fromTo(media,
          { yPercent: mobile ? -5 : -10, scale: mobile ? 1.06 : 1.12 },
          { yPercent: mobile ? 5 : 10, scale: 1, duration: 1 }, 0)
        .fromTo(content,
          { y: mobile ? 16 : 40 },
          { y: mobile ? -16 : -40, duration: 1 }, 0)
        .fromTo(photo,
          { filter: `blur(${mobile ? 1.4 : 2.4}px)` },
          { filter: 'blur(0px)', duration: 0.45 }, 0)
        .fromTo(dim,
          { opacity: 0.42 },
          { opacity: 0, duration: 0.45 }, 0);

      // SVG's "slice" fits the photo and car together. The travel endpoints sit outside
      // the visible crop, so the car enters and leaves the frame before each repeat.
      const travel = () => {
        const fit = Math.max(media.clientWidth / 822, media.clientHeight / 683);
        const halfVisible = section.clientHeight / fit / 2;
        return { from: 341.5 + halfVisible + 64, to: 341.5 - halfVisible - 64 };
      };
      const drives = [
        { car: forwardCar, reverse: false, duration: 7.2, phase: 0.2 },
        { car: reverseCar, reverse: true, duration: 8.4, phase: 0.55 },
      ].map(({ car, reverse, duration, phase }) => {
        const drive = gsap.fromTo(car, { y: () => reverse ? travel().to : travel().from }, {
          y: () => reverse ? travel().from : travel().to,
          duration,
          repeat: -1,
          repeatRefresh: true,
          ease: 'none',
          paused: true,
        });
        drive.progress(phase);
        return drive;
      });
      const visibility = new IntersectionObserver(([entry]) => {
        drives.forEach(drive => drive.paused(!entry.isIntersecting));
      });
      visibility.observe(section);
      return () => visibility.disconnect();
    });
  }
}
